import React from 'react';
import { useContext } from 'react';

import { Modal } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { ListContext } from '@contexts/ListContext';
import { useUserPermission } from '@contexts/UserContext';

import { forceReloadContact } from '@hooks/API/Contact/UseContact';
import { forceReloadContactBillingInfos } from '@hooks/API/Contact/UseContactBillingInfos';

import AddressItem from '@components/Connection/AddressItem';
import FlexContainer from '@components/Gui/FlexContainer';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import NwDivider from '@components/Gui/NwDivider';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import NwIcon from '@components/Gui/NwIcon';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Wrapper = styled.div`
    .ant-card {
        opacity: ${(props) => props.disabled && 0.5};

        .ant-card-head {
            .billinginfo-header {
                display: flex;
                font-size: 1rem !important;
                font-weight: ${(props) => (props.isDefault ? 'bold !important' : 'normal')};

                @media (max-width: 720px) {
                    font-size: 0.9rem !important;
                }
            }
        }

        .ant-card-body-content {
            .billinginfo-row {
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
                align-items: flex-start;

                p {
                    flex: 1 1 0;
                    strong {
                        padding-left: 0.5rem;
                    }
                }
            }
        }
    }
`;

const BillingInfoItem = ({ item, contactId, type, isDefault, onEditDoc }) => {
    const { cachedList } = useContext(ListContext);

    const invoicetypedefinitions =
        type.toLowerCase() === 'customer'
            ? cachedList.taxmethods.items.CustomerInvoiceTypes
            : cachedList.taxmethods.items.ModelInvoiceTypes;
    const invoiceType = invoicetypedefinitions.find((invtype) => invtype.Code === item.InvoiceType);

    const paymentTerm = item.PaymentTermCode
        ? cachedList.paymentTerms.items.find((pt) => pt.Code === item.PaymentTermCode)
        : null;

    const canChangeBillingInfos = useUserPermission('ChangeBillingInfos');

    const handleDisableItem = async () => {
        const data = {
            BillingInfoID: item.ID,
            ContactID: contactId,
        };
        try {
            await Axios.put(`/BillingInfos/DisableForContact`, data);
            forceReloadContactBillingInfos(contactId);
            forceReloadContact(contactId);
        } catch (error) {
            console.error('disable billing info error => ', error);
        }
    };

    const handleEnableItem = async () => {
        const data = {
            BillingInfoID: item.ID,
            ContactID: contactId,
        };
        try {
            await Axios.put(`/BillingInfos/EnableForContact`, data);
            forceReloadContactBillingInfos(contactId);
            forceReloadContact(contactId);
        } catch (error) {
            console.error('enable billing info error => ', error);
        }
    };

    const handleSetDefault = async () => {
        const data = {
            BillingInfoID: item.ID,
            ContactID: contactId,
        };
        try {
            await Axios.put(`/BillingInfos/SetAsDefaultForContact`, data);
            forceReloadContactBillingInfos(contactId);
        } catch (error) {
            console.error('set default billing info error => ', error);
        }
    };

    const handleUnlinkFromContact = async () => {
        const data = {
            BillingInfoID: item.ID,
            ContactID: contactId,
        };
        try {
            await Axios.put(`/BillingInfos/UnLinkFromContact`, data);
            forceReloadContactBillingInfos(contactId);
            forceReloadContact(contactId);
        } catch (error) {
            Modal.error({
                title: error.response.data.Message,
            });
            console.error('set default billing info error => ', error.response.data);
        }
    };

    const renderActions = () => {
        if (!canChangeBillingInfos) {
            return <></>;
        }
        return (
            <NwDropDownMenu
                size="small"
                actions={[
                    { name: '', key: 'edit', pinned: true, icon: byPrefixAndName.fasl['pen'], action: onEditDoc },
                    {
                        name: item.Disabled ? 'enable' : 'disable',
                        key: 'disabled',
                        icon: item.Disabled ? byPrefixAndName.fasl['check-circle'] : byPrefixAndName.fasl['ban'],
                        disabled: item.isDefault,
                        action: item.Disabled ? handleEnableItem : handleDisableItem,
                    },
                    {
                        name: 'set as default',
                        key: 'default',
                        icon: byPrefixAndName.fasl['bookmark'],
                        disabled: item.isDefault,
                        action: handleSetDefault,
                    },
                    {
                        name: 'remove from contact',
                        type: 'delete',
                        key: 'remove',
                        icon: byPrefixAndName.fasl['trash-alt'],
                        disabled: item.isDefault,
                        confirmationPlacement: 'left',
                        action: handleUnlinkFromContact,
                    },
                ]}
            />
        );
    };

    const getNameLabel = () => {
        if (type.toLowerCase() === 'model') {
            return 'Billing Name';
        }
        return 'Company Billing Name';
    };

    return (
        <Wrapper disabled={item.Disabled}>
            <NwCard
                title={
                    <div className="billinginfo-header">
                        {isDefault && (
                            <>
                                <NwIcon primary icon={byPrefixAndName.fass['bookmark']} />
                                &nbsp;
                            </>
                        )}
                        {item.Disabled && (
                            <>
                                <NwIcon icon={byPrefixAndName.fasl['ban']} />
                                &nbsp;
                            </>
                        )}
                        {item.CompanyName}
                    </div>
                }
                extra={renderActions()}
            >
                <NwDivider>General Info</NwDivider>
                <div className="billinginfo-row">
                    <p>
                        Billing Code: <strong>{item.BillingCode}</strong>
                    </p>
                </div>
                <div className="billinginfo-row">
                    <p>
                        {getNameLabel()}: <strong>{item.CompanyName}</strong>
                    </p>
                </div>
                <div className="billinginfo-row">
                    <p>
                        VAT #:<strong>{item.VatNumber}</strong>
                    </p>
                </div>
                <div className="billinginfo-row">
                    <p>
                        SSN / Tax ID / CF:<strong>{item.FiscalID}</strong>
                    </p>
                </div>
                <div className="billinginfo-row">
                    <p>
                        Billing Type:
                        <strong>
                            {invoiceType.Code} - {invoiceType.Name}
                        </strong>
                    </p>
                </div>
                {paymentTerm && (
                    <div className="billinginfo-row">
                        <p>
                            Default Payment Terms:
                            <strong>
                                {paymentTerm.Code}{' '}
                                {paymentTerm.Name !== paymentTerm.Code ? ` - ${paymentTerm.Name}` : ''}
                            </strong>
                        </p>
                    </div>
                )}
                {item.PEC || item.SDI ? (
                    <>
                        <div className="billinginfo-row">
                            <p>
                                PEC:<strong>{item.Pec}</strong>
                            </p>
                        </div>
                        <div className="billinginfo-row">
                            <p>
                                SDI:<strong>{item.SDI}</strong>
                            </p>
                        </div>
                    </>
                ) : null}
                <NwDivider>Address</NwDivider>
                <AddressItem address={item} contactID={contactId} readOnly />
                <NwDivider>Contact</NwDivider>
                <div className="billinginfo-row">
                    <p>
                        Name:<strong>{item.ContactName}</strong>
                    </p>
                </div>
                <div className="billinginfo-row">
                    <p>
                        Email:<strong>{item.ContactEmail}</strong>
                    </p>
                </div>
                <div className="billinginfo-row">
                    <p>
                        Phone:<strong>{item.ContactPhone}</strong>
                    </p>
                </div>
                {item.ContactName2 || item.ContactEmail2 || item.ContactPhone2 ? (
                    <>
                        <NwDivider>Alternative contact</NwDivider>
                        <div className="billinginfo-row">
                            <p>
                                Name:<strong>{item.ContactName2}</strong>
                            </p>
                        </div>
                        <div className="billinginfo-row">
                            <p>
                                Email:<strong>{item.ContactEmail2}</strong>
                            </p>
                        </div>
                        <div className="billinginfo-row">
                            <p>
                                Phone:<strong>{item.ContactPhone2}</strong>
                            </p>
                        </div>
                    </>
                ) : null}
            </NwCard>
        </Wrapper>
    );
};

export default BillingInfoItem;
